import { Injectable } from '@angular/core';
import {
	IPolLifeTest,
	IPolLifeTestConfig,
	IPolTestScoring,
	PolStatus,
	PolTestId,
} from '../../processes/models/processes.models';

/** Parámetros de fullScoring evaluables por prueba (alineados con pwcs). */
enum PolScoringParameter {
	PercentageTimeInTheMiddle = 'percentageTimeInTheMiddle',
	SimilarityPercentage = 'similarityPercentage',
	Emotion = 'emotion',
	EmotionTransition = 'emotionTransition',
	NoseVariance = 'noseVariance',
	NoseDistance = 'noseDistance',
	NoseTransition = 'noseTransition',
}

@Injectable({
	providedIn: 'root',
})
export class PolFailReasonService {
	private static readonly MIN_FULL_SCORING_CAPTURES = 3;
	private static readonly PERSISTENCE_FAIL_RATIO = 0.5;
	private static readonly PERSISTENCE_MIN_FAIL_COUNT = 2;
	private static readonly POL_FAIL_REASON = 'PROCESSES.DETAIL.POL_FAIL_REASON';

	/** Orden manual de evaluación por tipo de prueba (el primero en fallo persistente gana). */
	private static readonly PARAMETER_PRIORITY: Partial<Record<PolTestId, PolScoringParameter[]>> = (() => {
		const headTurnPriority: PolScoringParameter[] = [
			PolScoringParameter.NoseVariance,
			PolScoringParameter.NoseDistance,
			PolScoringParameter.NoseTransition,
			PolScoringParameter.EmotionTransition,
			PolScoringParameter.Emotion,
			PolScoringParameter.SimilarityPercentage,
		];
		const priority: Partial<Record<PolTestId, PolScoringParameter[]>> = {
			[PolTestId.MIDDLEFACE]: [
				PolScoringParameter.PercentageTimeInTheMiddle,
				PolScoringParameter.SimilarityPercentage,
			],
			[PolTestId.SMILE]: [
				PolScoringParameter.Emotion,
				PolScoringParameter.EmotionTransition,
				PolScoringParameter.PercentageTimeInTheMiddle,
				PolScoringParameter.SimilarityPercentage,
			],
		};
		for (const testId of [PolTestId.TURNRIGHTHEAD, PolTestId.TURNLEFTHEAD, PolTestId.LIFTHEAD]) {
			priority[testId] = headTurnPriority;
		}
		return priority;
	})();

	/** Claves i18n por prueba y parámetro (`public/i18n/es.json`, `en.json`). */
	private static readonly POL_FAIL_REASON_MESSAGES: Partial<
		Record<PolTestId, Partial<Record<PolScoringParameter, string>>>
	> = {
		[PolTestId.MIDDLEFACE]: {
			[PolScoringParameter.PercentageTimeInTheMiddle]: `${PolFailReasonService.POL_FAIL_REASON}.MIDDLE_FACE.NOT_CENTERED`,
			[PolScoringParameter.SimilarityPercentage]: `${PolFailReasonService.POL_FAIL_REASON}.MIDDLE_FACE.SIMILARITY_INSUFFICIENT`,
		},
		[PolTestId.SMILE]: {
			[PolScoringParameter.Emotion]: `${PolFailReasonService.POL_FAIL_REASON}.SMILE.EMOTION_INSUFFICIENT`,
			[PolScoringParameter.EmotionTransition]: `${PolFailReasonService.POL_FAIL_REASON}.SMILE.EMOTION_TRANSITION_UNNATURAL`,
			[PolScoringParameter.PercentageTimeInTheMiddle]: `${PolFailReasonService.POL_FAIL_REASON}.SMILE.NOT_CENTERED`,
			[PolScoringParameter.SimilarityPercentage]: `${PolFailReasonService.POL_FAIL_REASON}.SMILE.SIMILARITY_INSUFFICIENT`,
		},
		[PolTestId.TURNRIGHTHEAD]: {
			[PolScoringParameter.NoseVariance]: `${PolFailReasonService.POL_FAIL_REASON}.NOSE_MOVEMENT_UNNATURAL`,
			[PolScoringParameter.NoseDistance]: `${PolFailReasonService.POL_FAIL_REASON}.NOSE_DISTANCE_INSUFFICIENT`,
			[PolScoringParameter.NoseTransition]: `${PolFailReasonService.POL_FAIL_REASON}.NOSE_MOVEMENT_UNNATURAL`,
			[PolScoringParameter.EmotionTransition]: `${PolFailReasonService.POL_FAIL_REASON}.TURN_RIGHT.EMOTION_TRANSITION_UNNATURAL`,
			[PolScoringParameter.Emotion]: `${PolFailReasonService.POL_FAIL_REASON}.TURN_RIGHT.EMOTION_INSUFFICIENT`,
			[PolScoringParameter.SimilarityPercentage]: `${PolFailReasonService.POL_FAIL_REASON}.TURN_RIGHT.SIMILARITY_INSUFFICIENT`,
		},
		[PolTestId.TURNLEFTHEAD]: {
			[PolScoringParameter.NoseVariance]: `${PolFailReasonService.POL_FAIL_REASON}.NOSE_MOVEMENT_UNNATURAL`,
			[PolScoringParameter.NoseDistance]: `${PolFailReasonService.POL_FAIL_REASON}.NOSE_DISTANCE_INSUFFICIENT`,
			[PolScoringParameter.NoseTransition]: `${PolFailReasonService.POL_FAIL_REASON}.NOSE_MOVEMENT_UNNATURAL`,
			[PolScoringParameter.EmotionTransition]: `${PolFailReasonService.POL_FAIL_REASON}.TURN_LEFT.EMOTION_TRANSITION_UNNATURAL`,
			[PolScoringParameter.Emotion]: `${PolFailReasonService.POL_FAIL_REASON}.TURN_LEFT.EMOTION_INSUFFICIENT`,
			[PolScoringParameter.SimilarityPercentage]: `${PolFailReasonService.POL_FAIL_REASON}.TURN_LEFT.SIMILARITY_INSUFFICIENT`,
		},
		[PolTestId.LIFTHEAD]: {
			[PolScoringParameter.NoseVariance]: `${PolFailReasonService.POL_FAIL_REASON}.NOSE_MOVEMENT_UNNATURAL`,
			[PolScoringParameter.NoseDistance]: `${PolFailReasonService.POL_FAIL_REASON}.NOSE_DISTANCE_INSUFFICIENT`,
			[PolScoringParameter.NoseTransition]: `${PolFailReasonService.POL_FAIL_REASON}.NOSE_MOVEMENT_UNNATURAL`,
			[PolScoringParameter.EmotionTransition]: `${PolFailReasonService.POL_FAIL_REASON}.LIFT_HEAD.EMOTION_TRANSITION_UNNATURAL`,
			[PolScoringParameter.Emotion]: `${PolFailReasonService.POL_FAIL_REASON}.LIFT_HEAD.EMOTION_INSUFFICIENT`,
			[PolScoringParameter.SimilarityPercentage]: `${PolFailReasonService.POL_FAIL_REASON}.LIFT_HEAD.SIMILARITY_INSUFFICIENT`,
		},
	};

	private static readonly POL_FAIL_REASON_FALLBACK: Partial<Record<PolTestId, string>> = {
		[PolTestId.MIDDLEFACE]: `${PolFailReasonService.POL_FAIL_REASON}.MIDDLE_FACE.FALLBACK`,
		[PolTestId.SMILE]: `${PolFailReasonService.POL_FAIL_REASON}.SMILE.FALLBACK`,
		[PolTestId.TURNRIGHTHEAD]: `${PolFailReasonService.POL_FAIL_REASON}.TURN_RIGHT.FALLBACK`,
		[PolTestId.TURNLEFTHEAD]: `${PolFailReasonService.POL_FAIL_REASON}.TURN_LEFT.FALLBACK`,
		[PolTestId.LIFTHEAD]: `${PolFailReasonService.POL_FAIL_REASON}.LIFT_HEAD.FALLBACK`,
	};

	/** Umbrales por defecto alineados con pwcs `life-check-conf.ts`. */
	private static readonly DEFAULT_THRESHOLDS = {
		minPercentageTimeInMiddleMiddleFace: 90,
		minPercentageTimeInMiddleLifeTest: 25,
		minSimilarityPercentage: 50,
		minEmotion: 50,
		minEmotionTransition: 4,
		minNoseVariance: 60,
		minNoseDistance: 60,
		minNoseTransition: 5,
	};

	/**
	 * Determina el motivo principal de fallo de una prueba POL a partir de `fullScoring`.
	 * Aplica voto por persistencia (>= 50 % o >= 2 fallos) en orden de prioridad manual
	 * y, si no hay patrón persistente, usa la última captura.
	 */
	public getPolTestFailReasonFromFullScoring(lifeTest: IPolLifeTest): string {
		if (lifeTest.status === PolStatus.OnInit) {
			return `${PolFailReasonService.POL_FAIL_REASON}.NOT_STARTED`;
		}

		if (lifeTest.status !== PolStatus.OnFail) {
			return (
				PolFailReasonService.POL_FAIL_REASON_FALLBACK[lifeTest.lifeTestId] ??
				`${PolFailReasonService.POL_FAIL_REASON}.TEST_NOT_COMPLETED`
			);
		}

		const captures = lifeTest.fullScoring ?? [];

		if (captures.length < PolFailReasonService.MIN_FULL_SCORING_CAPTURES) {
			return `${PolFailReasonService.POL_FAIL_REASON}.INSUFFICIENT_CAPTURES`;
		}

		const persistentParameter = this.getPrimaryFailParameterByPersistence(
			lifeTest.lifeTestId,
			captures,
			lifeTest.conf,
		);

		if (persistentParameter) {
			return this.resolveFailMessage(lifeTest.lifeTestId, persistentParameter);
		}

		const lastSnapshot = captures[captures.length - 1];
		const fallbackParameter = this.getPrimaryFailParameterFromSnapshot(
			lifeTest.lifeTestId,
			lastSnapshot,
			lifeTest.conf,
		);

		if (fallbackParameter) {
			return this.resolveFailMessage(lifeTest.lifeTestId, fallbackParameter);
		}

		return (
			PolFailReasonService.POL_FAIL_REASON_FALLBACK[lifeTest.lifeTestId] ??
			`${PolFailReasonService.POL_FAIL_REASON}.TEST_NOT_COMPLETED`
		);
	}

	private isParameterFailing(
		parameter: PolScoringParameter,
		value: number,
		lifeTestId: PolTestId,
		conf?: IPolLifeTestConfig,
	): boolean {
		switch (parameter) {
			case PolScoringParameter.PercentageTimeInTheMiddle:
				if (lifeTestId === PolTestId.MIDDLEFACE) {
					return value < PolFailReasonService.DEFAULT_THRESHOLDS.minPercentageTimeInMiddleMiddleFace;
				}
				return value < PolFailReasonService.DEFAULT_THRESHOLDS.minPercentageTimeInMiddleLifeTest;
			case PolScoringParameter.SimilarityPercentage:
				return value < PolFailReasonService.DEFAULT_THRESHOLDS.minSimilarityPercentage;
			case PolScoringParameter.Emotion:
				return value < (conf?.minPercentageFaceBlendshape ?? PolFailReasonService.DEFAULT_THRESHOLDS.minEmotion);
			case PolScoringParameter.EmotionTransition:
				return (
					value < (conf?.minTransitionsFaceBlendshape ?? PolFailReasonService.DEFAULT_THRESHOLDS.minEmotionTransition)
				);
			case PolScoringParameter.NoseVariance:
				return value < (conf?.noseMovement?.minPercentageVariance ?? PolFailReasonService.DEFAULT_THRESHOLDS.minNoseVariance);
			case PolScoringParameter.NoseDistance:
				return value < (conf?.noseMovement?.minDistance ?? PolFailReasonService.DEFAULT_THRESHOLDS.minNoseDistance);
			case PolScoringParameter.NoseTransition:
				return value < (conf?.noseMovement?.minTransitions ?? PolFailReasonService.DEFAULT_THRESHOLDS.minNoseTransition);
			default:
				return false;
		}
	}

	private getRequiredFailCount(validCaptureCount: number): number {
		return Math.max(
			PolFailReasonService.PERSISTENCE_MIN_FAIL_COUNT,
			Math.ceil(validCaptureCount * PolFailReasonService.PERSISTENCE_FAIL_RATIO),
		);
	}

	private isParameterPersistentlyFailing(
		parameter: PolScoringParameter,
		captures: IPolTestScoring[],
		lifeTestId: PolTestId,
		conf?: IPolLifeTestConfig,
	): boolean {
		let failCount = 0;
		let validCount = 0;

		for (const snapshot of captures) {
			const rawValue = snapshot[parameter as keyof IPolTestScoring];
			if (rawValue === null || rawValue === undefined || typeof rawValue !== 'number') {
				continue;
			}
			validCount++;
			if (this.isParameterFailing(parameter, rawValue, lifeTestId, conf)) {
				failCount++;
			}
		}

		if (validCount === 0) {
			return false;
		}

		return failCount >= this.getRequiredFailCount(validCount);
	}

	private getPrimaryFailParameterFromSnapshot(
		lifeTestId: PolTestId,
		scoringSnapshot: IPolTestScoring,
		conf?: IPolLifeTestConfig,
	): PolScoringParameter | null {
		const priority = PolFailReasonService.PARAMETER_PRIORITY[lifeTestId];
		if (!priority?.length) {
			return null;
		}

		for (const parameter of priority) {
			const rawValue = scoringSnapshot[parameter as keyof IPolTestScoring];
			if (rawValue === null || rawValue === undefined || typeof rawValue !== 'number') {
				continue;
			}
			if (this.isParameterFailing(parameter, rawValue, lifeTestId, conf)) {
				return parameter;
			}
		}

		return null;
	}

	private getPrimaryFailParameterByPersistence(
		lifeTestId: PolTestId,
		captures: IPolTestScoring[],
		conf?: IPolLifeTestConfig,
	): PolScoringParameter | null {
		const priority = PolFailReasonService.PARAMETER_PRIORITY[lifeTestId];
		if (!priority?.length) {
			return null;
		}

		for (const parameter of priority) {
			if (this.isParameterPersistentlyFailing(parameter, captures, lifeTestId, conf)) {
				return parameter;
			}
		}

		return null;
	}

	private resolveFailMessage(lifeTestId: PolTestId, parameter: PolScoringParameter): string {
		return (
			PolFailReasonService.POL_FAIL_REASON_MESSAGES[lifeTestId]?.[parameter] ??
			PolFailReasonService.POL_FAIL_REASON_FALLBACK[lifeTestId] ??
			`${PolFailReasonService.POL_FAIL_REASON}.TEST_NOT_COMPLETED`
		);
	}
}
